import React, { useEffect, useState } from "react";
import styles from "./fbm-add-listings-drawer.module.css";
import { BxError, ProductCondition, SettingsBody, SkuGenerationMode } from "@boxem/core-api-schema";
import { Product, useInventoryContext } from "@/contexts/InventoryContext";
import { useUserContext } from "@/contexts/UserContext";
import { Offer } from "@/api/products";
import Button from "@/components/forms/Button/Button";
import { updateUserSettings } from "@/api/settings";
import CurrentOffers from "@/app/fba/CurrentOffers/CurrentOffers";
import ProductDetails from "@/app/fba/ProductDetails/ProductDetails";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconPlus from "@/components/icons/IconPlus";
import FbmAddListingForm from "../FbmAddListingForm/FbmAddListingForm";
import { FbmListingInput, useFbmListingContext } from "@/contexts/FbmListingContext";
import { ListingRestrictionInput, getListingRestrictions } from "@/api/fulfillment";
import { MockFormula, SkuFormula, SkuFormulaType } from "@/app/settings/variable_sku/variable_sku";
import { generateListPriceOptions } from "@/app/fba/utils";
import { ListPriceOption } from "@/app/fba/utils";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Flex from "@/components/Flex/Flex";
import Tabs, { TabItem } from "@/components/Tabs/Tabs";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";

export type AddFbmListingItemForm = {
  name: string; //Not needed when submitting.
  image: string; //Not needed when submitting.
  asin: string;
  sku: string;
  quantity: number;
  shippingTemplateId: number;
  condition: ProductCondition;
  conditionNotes?: string;
  buyCost?: number;
  price: ListPriceOption;
  expirationDate?: string;
  datePurchased?: string;
  supplier?: string;
  replenish?: boolean;
};

type FbmAddListingDrawerProps = {
  onClose: () => void;
  activeTab?: "form" | "details" | "offers";
  setActiveTab?: (tab: "form" | "details" | "offers") => void;
  product?: Product;
  sellerDataIndex?: number;
  defaultSku?: string;
  replenish?: boolean;
};

const FbmAddListingDrawer = ({
  product,
  sellerDataIndex,
  onClose,
  activeTab,
  setActiveTab,
  defaultSku,
  replenish,
}: FbmAddListingDrawerProps) => {
  const { addSupplier, addListingToList, editListingInList } = useFbmListingContext();
  const { getOffersForASIN, suppliers } = useInventoryContext();
  const { seller, setSeller } = useUserContext();
  const [ listingRestrictions, setListingRestrictions ] = useState<BxError[]>([]);
  const [skuGenerationOption, setSkuGenerationOption] = useState<SkuGenerationMode>((seller?.settings?.skuGenerationOption as SkuGenerationMode) ?? SkuGenerationMode.none);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState<boolean>(true);
  const [invalid, setInvalid] = useState<boolean>(false);
  const [formula, setFormula] = useState<SkuFormula>(!!seller?.settings?.skuGenerationFormula ? JSON.parse(seller?.settings?.skuGenerationFormula) : MockFormula);
  const [loadingAddRequest, setLoadingAddRequest] = useState<boolean>(false);
  const [listPriceOptions, setListPriceOptions] = useState<ListPriceOption[]>([]);
  
  const [addFbmListingItemForm, setAddFbmListingItemForm] = useState<AddFbmListingItemForm>({
    name: replenish ? product?.name ?? "" : "",
    image: replenish ? product?.image ?? "" : "",
    asin: replenish ? product?.asin ?? "" : "",
    sku: defaultSku,
    condition: replenish ? product.sellerData[0].condition : ProductCondition.New,
    conditionNotes: replenish ? product.sellerData[0].conditionNotes : "",
    buyCost: replenish ? parseFloat(product.buyCost) : null,
    price: replenish ? {
      price: product.sellerData[0].listPrice.toString(),
      fba: false,
      condition: product.sellerData[0].condition,
    } as ListPriceOption : null,
    quantity: 1,
    shippingTemplateId: replenish ? product.sellerData[0].shippingTemplateId : seller?.shippingTemplates[0]?.id ?? 0,
    expirationDate: replenish ? product.sellerData[0].expirationDate : undefined,
    datePurchased: replenish ? product.sellerData[0].datePurchased : undefined,
    supplier: replenish ? suppliers.find(sup => sup.id === product.sellerData[0].supplierId)?.name ?? undefined : undefined,
    replenish,
  });

  const handleOnClose = () => {
    if (loadingAddRequest) return;
    onClose();
    setAddFbmListingItemForm({
      name: "",
      image: "",
      asin: "",
      sku: "",
      condition: ProductCondition.New,
      conditionNotes: "",
      quantity: 1,
      shippingTemplateId: 0,
      buyCost: null,
      price: null,
      expirationDate: undefined,
      datePurchased: undefined,
      supplier: null,
    });
  };

  const incrementSkuCount = () => {
    const patch: SettingsBody = {
      id: seller.settings.id,
      skuGenerationCount: seller.settings.skuGenerationCount + 1,
    };

    updateUserSettings(patch).then((settings) => {
      seller.settings.skuGenerationCount = settings.skuGenerationCount;
      setSeller({...seller});
    });
  };

  const onAddFbmListingItem = async () => {
    setListingRestrictions([]);
    setLoadingAddRequest(true);
    const date = new Date();

    //Add lisitng to context array.
    const input: FbmListingInput = {
      name: product.name,
      asin: product.asin,
      sku: addFbmListingItemForm.sku,
      image: product.image,
      category: product.category,
      priceUSD: addFbmListingItemForm.price.price,
      buyCost: addFbmListingItemForm.buyCost?.toString() ?? "0", //Optional
      condition: addFbmListingItemForm.condition,
      quantity: replenish ? product.sellerData[0].quantity + addFbmListingItemForm.quantity : addFbmListingItemForm.quantity,
      shippingTemplateId: addFbmListingItemForm.shippingTemplateId,
      conditionNotes: addFbmListingItemForm.conditionNotes,
      datePurchased: addFbmListingItemForm.datePurchased, //Optional
      expirationDate: addFbmListingItemForm.expirationDate, //Optional
      supplier: addFbmListingItemForm.supplier, //Optional
      issues: [],
      errors: [],
      submittedAt: date.toISOString(),
      replenish: addFbmListingItemForm.replenish,
    };

    //Check the listing restrictions. If there are no problems then continue with listing creation.
    const listingRestrictionsInput: ListingRestrictionInput[] = [{
      asin: product.asin,
      condition: addFbmListingItemForm.condition,
    }];
    const listingRestrictions = await getListingRestrictions(listingRestrictionsInput);

    if (!!listingRestrictions[0] && listingRestrictions[0].length) {
      setListingRestrictions(listingRestrictions[0]);
      setLoadingAddRequest(false);
      return;
    }

    //Add listing to context state.
    addListingToList(input);

    //Increment SKU count
    if (skuGenerationOption === SkuGenerationMode.variable && !!formula.find(f => f.type === SkuFormulaType.count)) incrementSkuCount();

    //Add supplier
    if (!!addFbmListingItemForm.supplier) addSupplier(addFbmListingItemForm.supplier);

    onClose();
    setLoadingAddRequest(false)
  };

  const tabs: TabItem[] = [
    {
      key: "form",
      label: "Add product",
      children: (
        <FbmAddListingForm
          form={addFbmListingItemForm}
          setForm={(form: AddFbmListingItemForm) => setAddFbmListingItemForm(form)}
          product={product}
          sellerDataIndex={sellerDataIndex}
          defaultSku={defaultSku}
          offersLoading={offersLoading}
          listPriceOptions={listPriceOptions}
          setListPriceOptions={setListPriceOptions}
          listingRestrictions={listingRestrictions}
          loading={loadingAddRequest}
          setInvalid={(val: boolean) => setInvalid(val)}
          replenish={replenish}
        />
      ),
    },
    {
      key: "offers",
      disabled: offersLoading,
      label: offersLoading ? (
        <LoadingSpinner size="1rem" />
      ) : (
        <Flex gap={2} align="center">
          <span>Offers</span>
          <div className={styles.counter} style={{ marginLeft: "1ch" }}>
            <span>{offers.length}</span>
          </div>
        </Flex>
      ),
      children: (
        <CurrentOffers
          offers={offers}
          offersLoading={offersLoading}
          setPrice={(price: number) => {
            setAddFbmListingItemForm({
              ...addFbmListingItemForm,
              price: {
                price: price.toString(),
              },
            });
            setActiveTab("form");
          }}
        />
      ),
    },
    {
      key: "details",
      label: "Details",
      disabled: offersLoading,
      children: <ProductDetails product={product} offers={offers} />,
    },
  ];

  useEffect(() => {
    setOffersLoading(true);
    getOffersForASIN(product?.asin)
      .then(offers => {
        setListPriceOptions(generateListPriceOptions(offers));
        setOffers(offers);
      })
      .finally(() => {
        setOffersLoading(false);
      });
  }, [product]);

  return (
    <Modal
      open={!!product}
      onOpenChange={() => {}}
      onClose={handleOnClose}
      closeOnOutsideClick
      className={styles.modal}
    >
      <ModalHeader>
        <Flex align="center">
          <img src={product.image || "/images/product-placeholder.png"} alt="Product" width="32" height="32" />
          <Tooltip title={product.name}>
            <span className={`strong ${styles.productTitle}`} style={{ marginLeft: "1ch" }}>
              {product.name}
            </span>
          </Tooltip>
        </Flex>
      </ModalHeader>
      <ModalBody className={styles.modalBody}>
        <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={(e) => setActiveTab(e as "form" | "offers" | "details")} />
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between" align="center">
          <Button
            size="large"
            type="secondary"
            disabled={loadingAddRequest}
            onClick={handleOnClose}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            size="large"
            disabled={loadingAddRequest || invalid}
            onClick={async () => onAddFbmListingItem()}
          >
            {loadingAddRequest ? <LoadingSpinner /> : <IconPlus />}
            {replenish ? "Replenish Listing" : "Add Listing"}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default FbmAddListingDrawer;